import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";

import {
  OptionRow,
  getRecommendedSlot,
  AssignedResourcePanel,
  AssignedResourceNote,
  ALL_DURATIONS,
  DurationChips,
  groupDurationOptions,
  filterGroupByDuration,
  useGroupDuration
} from "/imports/ui/components/booking";
import { groupSlotsByLocation } from "../courtHelpers";

// Bottom sheet shared by the filter pickers and the multi-option picker.
function CourtFilterSheet({ court }) {
  const {
    currency,
    filters,
    setFilter,
    setFilters,
    availableSports,
    sportFilteredServices,
    availableFacilities,
    allowCourtPick,
    durationOptions,
    serviceLocations,
    expandedGroup,
    activeSheet,
    closeSheet,
    handleSportChange,
    handleSlotSelect,
    sheetTitles
  } = court;

  // Several lengths at the expanded time ("All durations"): the options sheet
  // leads with a chip strip and narrows the rows to one length.
  const [groupDuration, setGroupDuration] = useGroupDuration(expandedGroup);

  const renderSheetBody = () => {
    switch (activeSheet) {
      case "sport":
        return availableSports.map((s) => (
          <OptionRow
            key={s._id}
            label={s.name}
            selected={filters.sport === s._id}
            onClick={() => {
              handleSportChange(s._id);
              closeSheet();
            }}
          />
        ));
      case "service":
        return sportFilteredServices.map((s) => (
          <OptionRow
            key={s._id}
            label={s.name}
            selected={filters.serviceId === s._id}
            onClick={() => {
              setFilters({ serviceId: s._id, facilityId: null, duration: "" });
              closeSheet();
            }}
          />
        ));
      case "court":
        if (!allowCourtPick) return <AssignedResourcePanel noun="Court" names={availableFacilities.map((f) => f.name)} />;
        return (
          <>
            <OptionRow
              label="Any Court"
              selected={!filters.facilityId}
              onClick={() => {
                setFilter("facilityId", null);
                closeSheet();
              }}
            />
            {availableFacilities.map((f) => (
              <OptionRow
                key={f._id}
                label={f.name}
                selected={filters.facilityId === f._id}
                onClick={() => {
                  setFilter("facilityId", f._id);
                  closeSheet();
                }}
              />
            ))}
          </>
        );
      case "duration":
        return (
          <>
            {durationOptions.length > 1 && (
              <OptionRow
                label="All durations"
                sublabel="Pick a time first, then the length"
                selected={filters.duration === ALL_DURATIONS}
                onClick={() => {
                  setFilter("duration", ALL_DURATIONS);
                  closeSheet();
                }}
              />
            )}
            {durationOptions.map((opt) => {
              const label = opt.price
                ? `${opt.value} min — ${opt.isFrom ? "from " : ""}${(currency || "").toUpperCase()} ${opt.price}`
                : `${opt.value} min`;
              return (
                <OptionRow
                  key={opt.value}
                  label={label}
                  selected={filters.duration === String(opt.value)}
                  onClick={() => {
                    setFilter("duration", String(opt.value));
                    closeSheet();
                  }}
                />
              );
            })}
          </>
        );
      case "location":
        return (
          <>
            {serviceLocations.length > 1 && (
              <OptionRow
                label="All Locations"
                selected={!filters.locationId}
                onClick={() => {
                  setFilter("locationId", null);
                  closeSheet();
                }}
              />
            )}
            {serviceLocations.map((l) => (
              <OptionRow
                key={l._id}
                label={l.name}
                selected={filters.locationId === l._id}
                onClick={() => {
                  setFilter("locationId", l._id);
                  closeSheet();
                }}
              />
            ))}
          </>
        );
      case "options": {
        const groupDurations = groupDurationOptions(expandedGroup);
        const group = filterGroupByDuration(expandedGroup, groupDuration);
        const anySlot = getRecommendedSlot(group);
        const locationGroups = groupSlotsByLocation(group?.slots || []);
        const pickLocation = !allowCourtPick && locationGroups.length > 1;
        return (
          <>
            <DurationChips options={groupDurations} value={groupDuration} onChange={setGroupDuration} currency={currency} />
            {!pickLocation && anySlot && (
              <OptionRow label="Any Court" sublabel="Recommended — best availability" selected onClick={() => handleSlotSelect(anySlot)} />
            )}

            {pickLocation &&
              locationGroups.map((group) => {
                const slot = getRecommendedSlot(group);
                return (
                  <OptionRow
                    key={group.locationId}
                    label={group.locationName}
                    sublabel={`${(slot.currency || currency || "").toUpperCase()} ${parseFloat(slot.price || 0).toFixed(2)}`}
                    onClick={() => handleSlotSelect(slot)}
                  />
                );
              })}

            {!allowCourtPick && <AssignedResourceNote noun="Court" />}

            {allowCourtPick &&
              (group?.slots || []).map((slot) => (
                <OptionRow
                  key={`${slot.facilityId}-${slot.locationId}-${slot.pricingId}`}
                  label={slot.facilityName}
                  sublabel={[
                    !filters.locationId && slot.locationName,
                    `${(slot.currency || currency || "").toUpperCase()} ${parseFloat(slot.price || 0).toFixed(2)}`
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                  onClick={() => handleSlotSelect(slot)}
                />
              ))}
          </>
        );
      }
      default:
        return null;
    }
  };

  return (
    <Drawer
      anchor="bottom"
      open={!!activeSheet}
      onClose={closeSheet}
      PaperProps={{ sx: { borderTopLeftRadius: 20, borderTopRightRadius: 20, maxHeight: "75vh", px: 2.5, pt: 2, pb: 3 } }}
    >
      <Box sx={{ width: "2.5rem", height: "0.25rem", borderRadius: 2, bgcolor: "divider", mx: "auto", mb: 2 }} />
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
        <Typography sx={{ fontSize: "1.05rem", fontWeight: 700 }}>{sheetTitles[activeSheet] || ""}</Typography>
        <IconButton size="small" onClick={closeSheet} aria-label="Close">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1, overflowY: "auto" }}>{renderSheetBody()}</Box>
    </Drawer>
  );
}

export default CourtFilterSheet;
